import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyChatRooms } from "../../services/chatService";
import Loader from "../../components/common/Loader";
import { toast } from "react-toastify";

const RoomList = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getMyChatRooms("Personal")
      .then((res) => setRooms(res.data?.Result || []))
      .catch(() => toast.error("Failed to load rooms."))
      .finally(() => setLoading(false));
  }, []);

  const handleRoomClick = (room) => {
    navigate(`/rooms/${room.Id}/galleries`, { state: { room } });
  };

  if (loading) return <Loader />;

  return (
    <div>
      <h2 style={styles.heading}>
        @ Rooms
        <span style={styles.count}>{rooms.length} room{rooms.length !== 1 ? "s" : ""}</span>
      </h2>

      {rooms.length === 0 ? (
        <p style={{ color: "#888", fontSize: 14 }}>No rooms found.</p>
      ) : (
        <div style={styles.list}>
          {rooms.map((room) => (
            <div key={room.Id} style={styles.card} onClick={() => handleRoomClick(room)}>
              {/* Left: avatar */}
              <div style={styles.avatarWrap}>
                {room.LogoUrl ? (
                  <img
                    src={room.LogoUrl.replace("\\", "/")}
                    alt={room.Name}
                    style={styles.avatar}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div style={styles.avatarFallback}>
                    {room.Name?.charAt(0).toUpperCase() || "R"}
                  </div>
                )}
              </div>

              {/* Middle: info */}
              <div style={styles.info}>
                <div style={styles.roomName}>{room.Name}</div>

                {/* Last message preview */}
                {room.LastMessage && room.LastMessage.length > 0 && (
                  <div style={styles.lastMsg}>
                    {room.LastMessage[0].MessageType === "image"
                      ? "📷 Photo"
                      : room.LastMessage[0].MessageType === "video"
                      ? "🎥 Video"
                      : room.LastMessage[0].Message}
                  </div>
                )}

                <div style={styles.meta}>
                  <span>👥 {room.TotalUsers} users</span>
                  <span>🎉 {room.TotalEvents} events</span>
                  <span style={styles.roomType}>{room.RoomType}</span>
                </div>
              </div>

              {/* Right: unread badge + time */}
              <div style={styles.right}>
                {room.LastMessage?.[0]?.Timestamp && (
                  <div style={styles.time}>
                    {new Date(room.LastMessage[0].Timestamp).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                )}
                {room.UnreadMessageCount > 0 && (
                  <div style={styles.unreadBadge}>{room.UnreadMessageCount}</div>
                )}
                {room.ChatRoomLink && (
                  <a
                    href={room.ChatRoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.linkBtn}
                    onClick={(e) => e.stopPropagation()}
                  >
                    🔗
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  heading: {
    margin: "0 0 24px",
    fontSize: 22,
    fontWeight: 700,
    color: "#1e1e2d",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  count: {
    fontSize: 13,
    fontWeight: 400,
    color: "#888",
    background: "#f0f0f0",
    padding: "2px 12px",
    borderRadius: 20,
  },
  list: { display: "flex", flexDirection: "column", gap: 12, maxWidth: 680 },
  card: {
    background: "#fff",
    borderRadius: 14,
    padding: "16px 20px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    cursor: "default",
  },
  avatarWrap: { flexShrink: 0 },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "2px solid #ede9ff",
  },
  avatarFallback: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "#6c63ff",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 20,
  },
  info: { flex: 1, minWidth: 0 },
  roomName: { fontWeight: 700, fontSize: 15, color: "#1e1e2d", marginBottom: 4 },
  lastMsg: {
    fontSize: 13,
    color: "#888",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 6,
    maxWidth: 380,
  },
  meta: { display: "flex", gap: 14, fontSize: 12, color: "#666" },
  roomType: {
    background: "#ede9ff",
    color: "#6c63ff",
    padding: "1px 8px",
    borderRadius: 20,
    fontWeight: 600,
  },
  right: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
    flexShrink: 0,
  },
  time: { fontSize: 11, color: "#aaa" },
  unreadBadge: {
    background: "#6c63ff",
    color: "#fff",
    borderRadius: "50%",
    width: 20,
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
  },
  linkBtn: {
    fontSize: 16,
    textDecoration: "none",
    lineHeight: 1,
  },
};

export default RoomList;
